import { useState } from 'react';
import { Plus, Search, Filter, RefreshCw, Loader2 } from 'lucide-react';
import { useConsumables, useStockMovements } from '../hooks';
import { ConsumableCard, ConsumableForm, StockMovementForm, LowStockAlert } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import type { Consumable, CreateConsumableDTO, StockInDTO, StockOutDTO } from '@yawi/types';

export const ConsumablesPage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const ability = useAbility();
  const canManage = ability.can('manage', 'Consumable');
  const canCreateMovement = ability.can('create', 'StockMovement');

  const {
    consumables,
    lowStockItems,
    pagination,
    loading,
    error,
    params,
    search,
    filterByLowStock,
    goToPage,
    createConsumable,
    refresh,
    refreshLowStock,
  } = useConsumables();

  const { stockIn, stockOut } = useStockMovements();

  const [showForm, setShowForm] = useState(false);
  const [showStockForm, setShowStockForm] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState<Consumable | null>(null);
  const [movementType, setMovementType] = useState<'IN' | 'OUT'>('IN');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    search(searchTerm);
  };

  const handleCreateConsumable = async (data: CreateConsumableDTO) => {
    setFormLoading(true);
    try {
      await createConsumable(data);
      setShowForm(false);
      toast.success(t('inventory.consumableCreated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleStockMovement = async (data: StockInDTO | StockOutDTO) => {
    if (!selectedConsumable) return;
    setFormLoading(true);
    try {
      if (movementType === 'IN') {
        await stockIn(selectedConsumable.id, data as StockInDTO);
        toast.success(t('inventory.stockInSuccess'));
      } else {
        await stockOut(selectedConsumable.id, data as StockOutDTO);
        toast.success(t('inventory.stockOutSuccess'));
      }
      setShowStockForm(false);
      setSelectedConsumable(null);
      refresh();
      refreshLowStock();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const openStockIn = (consumable: Consumable) => {
    setSelectedConsumable(consumable);
    setMovementType('IN');
    setShowStockForm(true);
  };

  const openStockOut = (consumable: Consumable) => {
    setSelectedConsumable(consumable);
    setMovementType('OUT');
    setShowStockForm(true);
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('inventory.consumables')}</h1>
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.createConsumable')}
          </button>
        )}
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="mb-6">
          <LowStockAlert
            items={lowStockItems}
            onItemClick={canCreateMovement ? openStockIn : undefined}
            onViewAll={() => filterByLowStock(true)}
          />
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('inventory.searchConsumables')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => { refresh(); refreshLowStock(); }}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </form>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={params.lowStock === true}
                  onChange={(e) => filterByLowStock(e.target.checked ? true : undefined)}
                  className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">{t('inventory.showLowStockOnly')}</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Consumables Grid */}
      {!loading && (
        <div className="grid gap-4 md:grid-cols-2">
          {consumables.map((consumable) => (
            <ConsumableCard
              key={consumable.id}
              consumable={consumable}
              onStockIn={canCreateMovement ? () => openStockIn(consumable) : undefined}
              onStockOut={canCreateMovement ? () => openStockOut(consumable) : undefined}
              showActions={canCreateMovement}
            />
          ))}

          {consumables.length === 0 && (
            <div className="col-span-2 text-center py-12 text-slate-500">
              <p>{t('inventory.noConsumables')}</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={!pagination.hasPreviousPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-1 text-sm text-slate-600">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Consumable Form Modal */}
      {showForm && (
        <ConsumableForm
          onSubmit={handleCreateConsumable}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* Stock Movement Form Modal */}
      {showStockForm && selectedConsumable && (
        <StockMovementForm
          consumableName={selectedConsumable.name}
          currentStock={selectedConsumable.currentStock}
          movementType={movementType}
          onSubmit={handleStockMovement}
          onCancel={() => {
            setShowStockForm(false);
            setSelectedConsumable(null);
          }}
          loading={formLoading}
        />
      )}
    </div>
  );
};
